import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Card, Button, Badge } from './ui';
import groupService from '../services/groupService';
import {
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon
} from '@heroicons/react/24/outline';

const UnsubscribeProcessMonitor = ({ processId, onClose }) => {
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch process status with auto-refresh
  const {
    data: processStatus,
    isLoading,
    refetch
  } = useQuery(
    ['unsubscribeProcess', processId],
    () => groupService.getUnsubscribeProcessStatus(processId),
    {
      enabled: !!processId,
      refetchInterval: autoRefresh ? 2000 : false,
      refetchIntervalInBackground: false,
      onError: (err) => {
        console.error('Error fetching unsubscribe process status:', err);
      }
    }
  );

  // Auto-close when process completes
  useEffect(() => {
    if (processStatus?.status === 'completed' || processStatus?.status === 'failed') {
      const timer = setTimeout(() => {
        setAutoRefresh(false);
      }, 10000); // Stop auto-refresh after 10 seconds when completed

      return () => clearTimeout(timer);
    }
  }, [processStatus?.status]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="yellow">Processing</Badge>;
      case 'completed':
        return <Badge variant="green">Completed</Badge>;
      case 'failed':
        return <Badge variant="red">Failed</Badge>;
      case 'cancelled':
        return <Badge variant="gray">Cancelled</Badge>;
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  const getProgressPercentage = () => {
    if (!processStatus) return 0;
    return Math.round((processStatus.processed / processStatus.totalGroups) * 100);
  };

  const cancelProcess = async () => {
    try {
      await groupService.cancelUnsubscribeProcess(processId);
      refetch();
    } catch (error) {
      console.error('Error cancelling unsubscribe process:', error);
    }
  };

  if (isLoading && !processStatus) {
    return (
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">Loading process status...</div>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }

  if (!processStatus) {
    return (
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-red-600">Process not found</div>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-medium text-gray-900">
              Batch Unsubscribe Process
            </h3>
            {getStatusBadge(processStatus.status)}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setAutoRefresh(!autoRefresh)}
              icon={autoRefresh ? PauseIcon : PlayIcon}
            >
              {autoRefresh ? 'Pause' : 'Resume'}
            </Button>
            <Button size="sm" variant="secondary" onClick={onClose}>
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Progress</span>
            <span>{getProgressPercentage()}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                processStatus.status === 'completed' 
                  ? 'bg-green-600' 
                  : processStatus.status === 'failed'
                  ? 'bg-red-600'
                  : 'bg-blue-600'
              }`}
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">{processStatus.totalGroups}</div>
            <div className="text-sm text-gray-500">Total</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{processStatus.processed}</div>
            <div className="text-sm text-gray-500">Processed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{processStatus.successful}</div>
            <div className="text-sm text-gray-500">Successful</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{processStatus.failed}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">{processStatus.skipped || 0}</div>
            <div className="text-sm text-gray-500">Skipped</div>
          </div>
        </div>

        {/* Process Info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Process ID:</span>
            <span className="ml-2 text-gray-600 font-mono">{processStatus.processId}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Started:</span>
            <span className="ml-2 text-gray-600">
              {new Date(processStatus.startedAt).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Delay Between:</span>
            <span className="ml-2 text-gray-600">
              {processStatus.config?.delayBetween || 1000}ms
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Estimated Time:</span>
            <span className="ml-2 text-gray-600">
              {processStatus.totalGroups && processStatus.config?.delayBetween
                ? `~${Math.round((processStatus.totalGroups * processStatus.config.delayBetween) / 60000)} min`
                : 'Calculating...'}
            </span>
          </div>
        </div>

        {/* Actions */}
        {processStatus.status === 'in_progress' && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="danger"
              onClick={cancelProcess}
            >
              Cancel Process
            </Button>
          </div>
        )}

        {/* Logs - Show ALL logs */}
        {processStatus.logs && processStatus.logs.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">
                Process Logs ({processStatus.logs.length} total)
              </h4>
              <div className="text-xs text-gray-500">
                Success: {processStatus.logs.filter(l => l.message?.includes('✅')).length} | 
                Failed: {processStatus.logs.filter(l => l.message?.includes('❌')).length} | 
                Skipped: {processStatus.logs.filter(l => l.message?.includes('⏭️')).length} |
                Unauthorized: {processStatus.logs.filter(l => l.message?.includes('🚫')).length}
              </div>
            </div>
            <div className="bg-gray-50 rounded-md p-3 max-h-96 overflow-y-auto border">
              <div className="space-y-1">
                {processStatus.logs.map((log, index) => (
                  <div key={index} className="text-xs flex items-start hover:bg-gray-100 p-1 rounded">
                    <span className="text-gray-500 font-mono flex-shrink-0 w-16">
                      {new Date(log.time).toLocaleTimeString()}
                    </span>
                    <span className={`flex-1 min-w-0 break-words ml-2 ${
                      log.message?.includes('✅') ? 'text-green-700' :
                      log.message?.includes('❌') ? 'text-red-700' :
                      log.message?.includes('🚫') ? 'text-orange-700' :
                      log.message?.includes('⏭️') ? 'text-yellow-700' :
                      log.message?.includes('👤') ? 'text-blue-700' :
                      log.message?.includes('📊') ? 'text-blue-700' :
                      log.message?.includes('🎯') ? 'text-green-700' :
                      log.message?.includes('🛑') ? 'text-gray-700' :
                      'text-gray-700'
                    }`}>
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default UnsubscribeProcessMonitor;
